import React, { useEffect, useState, useCallback } from "react";
import ExportButton from "./ExportButton";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomerService } from "../services/CustomerService";
import { useListParams } from "hooks/useListParams";
import Pagination from "./common/Pagination";
import Drawer, { FilterDrawer } from "./common/Drawer";
import { companyPath } from "./Company/CompanyContext";

interface Company {
  id: number;
  name: string;
  apiKey: string;
  phone?: string;
  email?: string;
}

type SortField = 'name' | 'id';

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const emptyFilters = { name: '', phone: '', email: '' };
const emptyCompanyForm = { name: '', phone: '', email: '' };

const PAGE_SIZE = 10;

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const { pageNumber, setPageNumber, sortField, setSort, appliedFilters, setAppliedFilters } =
    useListParams<typeof emptyFilters, SortField>(emptyFilters, { field: 'name', direction: 'asc' });
  const [customers, setCustomers] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  // Sync tempFilters when sidebar opens
  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  const activeFilterCount = Object.values(appliedFilters).filter(v => v !== '').length;

  // Company form in the side panel: "new" for Add Company, or the company whose settings are open.
  const [formTarget, setFormTarget] = useState<Company | "new" | null>(null);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formSaving, setFormSaving] = useState(false);

  const loadCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await CustomerService.customer.getCustomers(
        sortField,
        pageNumber,
        PAGE_SIZE,
        appliedFilters.name || undefined,
        appliedFilters.phone || undefined,
        appliedFilters.email || undefined,
      );
      setCustomers(response.data.items ?? []);
      setTotalCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setIsLoading(false);
    }
  }, [sortField, pageNumber, appliedFilters]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleSort = (field: SortField) => {
    setSort(field, 'asc');
  };

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const openCompanyForm = (target: Company | "new") => {
    setCompanyForm(target === "new"
      ? emptyCompanyForm
      : { name: target.name, phone: target.phone ?? '', email: target.email ?? '' });
    setFormErrors({});
    setFormTarget(target);
  };

  const handleCompanySave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTarget) return;
    const errors: Record<string, string> = {};
    if (!companyForm.name.trim()) errors.name = 'Name is required';
    if (!companyForm.phone.trim()) errors.phone = 'Phone is required';
    if (!companyForm.email.trim()) errors.email = 'Email is required';
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    const payload = {
      name: companyForm.name.trim(),
      phone: companyForm.phone.trim(),
      email: companyForm.email.trim(),
    };
    setFormSaving(true);
    try {
      if (formTarget === "new") {
        const response = await CustomerService.customer.createCustomer(payload);
        setFormTarget(null);
        // Open the new company straight away so its API keys can be added next.
        if (response.data?.id) navigate(companyPath(response.data.id, 'api-keys'));
        else await loadCustomers();
      } else {
        await CustomerService.customer.updateCustomer(formTarget.id, payload);
        toast.success('Company updated successfully');
        setFormTarget(null);
        await loadCustomers();
      }
    } catch {
      setFormErrors({ general: formTarget === "new" ? 'Failed to create company' : 'Failed to save company' });
    } finally {
      setFormSaving(false);
    }
  };

  const handleExport = async (format: string) => {
    setIsExporting(true);
    try {
      const response = await CustomerService.customer.exportCustomers(
        sortField,
        appliedFilters.name || undefined,
        appliedFilters.phone || undefined,
        appliedFilters.email || undefined,
        format,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `companies.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      console.error("Failed to export companies");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && customers.length === 0)
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <div className="container-fluid p-4">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard" className="text-decoration-none">Home</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Companies</li>
        </ol>
      </nav>

      <FilterDrawer
        title="Filter Companies"
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={handleClearAllFilters}
        onApply={handleApplyFilters}
      >
        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Company Name</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name..."
              name="name"
              value={tempFilters.name}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Phone</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by phone..."
              name="phone"
              value={tempFilters.phone}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Email</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by email..."
              name="email"
              value={tempFilters.email}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>
      </FilterDrawer>

      <Drawer
        isOpen={formTarget !== null}
        onClose={() => setFormTarget(null)}
        title={formTarget === "new" ? "Add Company" : "Company Settings"}
        icon={formTarget === "new" ? "plus-circle" : "gear"}
        width={480}
      >
        <form onSubmit={handleCompanySave}>
          {formErrors.general && (
            <div className="alert alert-danger">{formErrors.general}</div>
          )}
          <div className="mb-3">
            <label htmlFor="company-name" className="form-label">Company Name <span className="text-danger">*</span></label>
            <input
              type="text"
              id="company-name"
              className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
              value={companyForm.name}
              onChange={(e) => setCompanyForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Enter company name"
              autoFocus
            />
            {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
          </div>
          <div className="mb-3">
            <label htmlFor="company-phone" className="form-label">Phone Number <span className="text-danger">*</span></label>
            <input
              type="tel"
              id="company-phone"
              className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
              value={companyForm.phone}
              onChange={(e) => setCompanyForm(f => ({ ...f, phone: formatPhoneNumber(e.target.value) }))}
              placeholder="(123) 456-7890"
            />
            {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
          </div>
          <div className="mb-4">
            <label htmlFor="company-email" className="form-label">Email <span className="text-danger">*</span></label>
            <input
              type="email"
              id="company-email"
              className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
              value={companyForm.email}
              onChange={(e) => setCompanyForm(f => ({ ...f, email: e.target.value }))}
              placeholder="example@company.com"
            />
            {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setFormTarget(null)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={formSaving}>
              {formSaving
                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Saving...</>
                : 'Save Company'}
            </button>
          </div>
        </form>
      </Drawer>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h3 className="mb-0">Companies</h3>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={loadCustomers}
            disabled={isLoading}
          >
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => openCompanyForm("new")}>
            <i className="bi bi-plus-circle me-1"></i>Add Company
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th scope="col" onClick={() => handleSort('id')} style={{ cursor: "pointer" }}>
                    ID {sortField === 'id' && <i className="bi bi-arrow-up ms-1"></i>}
                  </th>
                  <th scope="col" onClick={() => handleSort('name')} style={{ cursor: "pointer" }}>
                    Company Name {sortField === 'name' && <i className="bi bi-arrow-up ms-1"></i>}
                  </th>
                  <th scope="col">Phone</th>
                  <th scope="col">Email</th>
                  <th scope="col" className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="clickable-row"
                    onClick={() => navigate(companyPath(customer.id))}
                  >
                    <td>{customer.id}</td>
                    <td>
                      <Link
                        to={companyPath(customer.id)}
                        className="fw-medium text-decoration-none"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {customer.name}
                      </Link>
                    </td>
                    <td>{customer.phone || '-'}</td>
                    <td>{customer.email || '-'}</td>
                    <td className="text-end text-nowrap">
                      <Link
                        to={companyPath(customer.id, 'api-keys')}
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={(e) => e.stopPropagation()}
                        title="Go straight to this company's API keys"
                      >
                        <i className="bi bi-key me-1"></i>API Keys
                      </Link>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCompanyForm(customer);
                        }}
                        title="Company settings"
                      >
                        Settings
                      </button>
                      <Link
                        to={companyPath(customer.id)}
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Open<i className="bi bi-chevron-right ms-1"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {customers.length === 0 && !isLoading && (
            <div className="alert alert-info text-center m-3">
              No companies found.
            </div>
          )}
        </div>
      </div>

      <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
    </div>
  );
};

export default Companies;
